import { ref, inject, computed } from "vue";
import { useRouter } from "vue-router";
import Toast from "@/plugins/Toast";
import { ToastType } from "@/types/toast";
import AuthRepository from "@/repositories/AuthRepository";
import { useDisplay } from "vuetify";

export default class RegisterViewModel {
  public step = ref(1);
  public dni = ref("");
  public email = ref("");
  public phoneNumber = ref("");
  public idSocio = ref("");
  public password = ref("");
  public question = ref("");
  public answer = ref("");
  public isPwdVisible = ref(false);
  public isAnswerVisible = ref(false);
  public loading = ref(false);
  public appName = process.env.VUE_APP_APP_NAME || "";
  public whatsapp = process.env.VUE_APP_WHATSAPP || "";
  public webPage = process.env.VUE_APP_WEBPAGE || "";

  // Validation rules
  public dniRules = [(v: string) => !!v || "El DNI es obligatorio"];
  public emailRules = [
    (v: string) => !!v || "El correo electrónico es obligatorio",
    (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Correo inválido",
  ];
  public phoneRules = [
    (v: string) => !!v || "El número de teléfono es obligatorio",
  ];
  public idSocioRules = [(v: string) => !!v || "El ID de socio es obligatorio"];
  public passwordRules = [
    (v: string) => !!v || "La contraseña es obligatoria",
    (v: string) => v.length >= 6 || "Debe tener al menos 6 caracteres",
    (v: string) =>
      /^(?=.*[A-Z])(?=.*\d)/.test(v) ||
      "Debe contener al menos una letra mayúscula y un número",
  ];
  public answerRules = [
    (v: string) => !!v || "La respuesta es obligatoria",
    (v: string) =>
      /^[a-zA-Z0-9ÁÉÍÓÚáéíóúÑñ\s.,\-'"¡!¿?(){}[\]]+$/.test(v) ||
      "La respuesta contiene caracteres no permitidos",
  ];

  public secretQuestions = [
    "¿Cuál es el nombre de tu primera mascota?",
    "¿Cuál fue tu primer concierto al que asististe?",
    "¿Cuál es el nombre del primer amigo/a que hiciste en la escuela?",
    "¿En qué ciudad conociste a tu pareja?",
    "¿Cuál es el nombre del primer/a jefe/a que tuviste?",
    "¿En qué país te gustaría vivir si pudieras elegir?",
    "¿Cuál es tu plato favorito que sabes cocinar?",
    "¿Cuál es el nombre de la primera calle en la que viviste?",
  ];

  private toast: Toast;
  private router = useRouter();

  // Add Vuetify's display composable
  private display = useDisplay();

  // Computed property for dynamic stepper labels
  public stepLabels = computed(() => {
    if (this.display.mdAndDown.value) {
      return [
        "DNI/RUC",
        "Correo",
        "Teléfono",
        "Pregunta Secreta",
        "Contraseña",
      ];
    } else {
      return [
        "DNI/RUC",
        "Correo",
        "Teléfono",
        "Pregunta Secreta",
        "Contraseña",
      ];
    }
  });

  // Images
  logoImage: string;

  constructor() {
    this.toast = inject("toast") as Toast;

    this.logoImage = require("@/assets/images/logo.png");
  }

  toggleAnswerVisibility = (): void => {
    this.isAnswerVisible.value = !this.isAnswerVisible.value;
  };
  async validateCurrentStep(): Promise<boolean> {
    switch (this.step.value) {
      case 1:
        return await this.checkAssociate();
      case 2:
        return await this.confirmEmail();
      case 3:
        return await this.confirmPhoneNumber();
      case 4:
        return await this.confirmSecretQuestion();
      case 5:
        return await this.confirmPassword();
      default:
        return false;
    }
  }

  previousStep(): void {
    if (this.step.value > 1) {
      this.step.value--;
    }
  }

  nextStep = async (): Promise<void> => {
    if (this.step.value < 6) {
      const isValid = await this.validateCurrentStep(); // Validate the current step
      if (isValid) {
        this.step.value++; // Proceed to the next step if valid
      }
    } else {
      console.log("final");
    }
  };

  async checkAssociate(): Promise<boolean> {
    if (!this.dni.value) {
      this.toast.showToast(
        "Ingrese su Documento de Identidad para continuar.",
        ToastType.Info
      );
      return false;
    }

    this.loading.value = true;

    try {
      const response = await AuthRepository.checkAssociate(this.dni.value);
      const { status } = response.data;
      const code = response.status;
      if (status) {
        this.toast.showToast(
          "Ud ya se encuentra afiliado con nosotros.",
          ToastType.Success
        );
        return false;
      }

      if (code === 201) {
        this.toast.showToast(
          `No hemos encontrado tu registro en nuestra cooperativa.  
Te invitamos a acercarte a una de nuestras agencias en ${this.appName} para afiliarte.  
Si necesitas más información, no dudes en contactarnos a través de WhatsApp. ¡Estamos para servirte!`,
          ToastType.Info
        );
        return false;
      }

      if (code === 202) {
        return true;
      }

      this.toast.showToast(
        "Respuesta inesperada del servidor.",
        ToastType.Error
      );
      return false;
    } catch (error) {
      console.error("Error al verificar el asociado:", error);
      this.toast.showToast("Error de servidor.", ToastType.Error);
      return false;
    } finally {
      this.loading.value = false;
    }
  }

  async confirmEmail(): Promise<boolean> {
    if (!this.email.value) {
      this.toast.showToast(
        "El correo electrónico es obligatorio.",
        ToastType.Error
      );
      return false;
    }
    this.loading.value = true;
    try {
      const response = await AuthRepository.confirmEmail(
        this.dni.value,
        this.email.value
      );
      if (response.status) {
        this.toast.showToast(
          "Correo confirmado correctamente.",
          ToastType.Success
        );
        return true;
      } else {
        this.toast.showToast("Este correo ya está en uso.", ToastType.Error);
        return false;
      }
    } catch (error) {
      this.toast.showToast("Error al confirmar el correo.", ToastType.Error);
      return false;
    } finally {
      this.loading.value = false;
    }
  }

  async confirmPhoneNumber(): Promise<boolean> {
    if (!this.phoneNumber.value || !this.idSocio.value) {
      this.toast.showToast(
        "El número de teléfono y el ID de socio son obligatorios.",
        ToastType.Error
      );
      return false;
    }
    this.loading.value = true;
    try {
      const response = await AuthRepository.confirmPhoneNumber(
        this.dni.value,
        this.phoneNumber.value,
        this.idSocio.value
      );
      if (response.status) {
        this.toast.showToast(
          "Teléfono confirmado correctamente.",
          ToastType.Success
        );
        return true;
      } else {
        this.toast.showToast(
          "Los datos ingresados son incorrectos.",
          ToastType.Error
        );
        return false;
      }
    } catch (error) {
      this.toast.showToast(
        "Error al confirmar los datos. Inténtelo más tarde.",
        ToastType.Error
      );
      return false;
    } finally {
      this.loading.value = false;
    }
  }

  async confirmSecretQuestion(): Promise<boolean> {
    if (!this.question.value) {
      this.toast.showToast(
        "Debes seleccionar una pregunta secreta.",
        ToastType.Error
      );
      return false;
    }

    if (!this.answer.value) {
      this.toast.showToast(
        "Debes proporcionar una respuesta a la pregunta secreta.",
        ToastType.Error
      );
      return false;
    }
    // Validar la respuesta secreta con las reglas definidas
    for (const rule of this.answerRules) {
      const result = rule(this.answer.value);
      if (result !== true) {
        // Si no pasa la validación, mostrar un toast con el mensaje de error
        this.toast.showToast(result, ToastType.Error);
        return false;
      }
    }
    return true;
  }

  async confirmPassword(): Promise<boolean> {
    if (!this.password.value) {
      this.toast.showToast("La contraseña es obligatoria.", ToastType.Error);
      return false;
    }
    for (const rule of this.passwordRules) {
      const result = rule(this.password.value);
      if (result !== true) {
        // Si no pasa la validación, mostrar un toast con el mensaje de error
        this.toast.showToast(result, ToastType.Error);
        return false;
      }
    }
    this.loading.value = true;
    try {
      const response = await AuthRepository.confirmPassword({
        dni: this.dni.value,
        question: this.question.value,
        answer: this.answer.value,
        password: this.password.value,
        email: this.email.value,
      });
      if (response.status) {
        this.toast.showToast("Registro completado.", ToastType.Success);
        this.resetForm();
        return true;
      } else {
        this.toast.showToast(
          "Error al completar el registro.",
          ToastType.Error
        );
        return false;
      }
    } catch (error) {
      this.toast.showToast("Error al completar el registro.", ToastType.Error);
      return false;
    }
  }
  resetForm(): void {
    this.step.value = 1;
    this.dni.value = "";
    this.email.value = "";
    this.phoneNumber.value = "";
    this.idSocio.value = "";
    this.password.value = "";
    this.question.value = "";
    this.answer.value = "";
    this.isPwdVisible.value = false;
    this.isAnswerVisible.value = false;
    this.loading.value = false;
  }
  togglePasswordVisibility = (): void => {
    this.isPwdVisible.value = !this.isPwdVisible.value;
  };

  goToWeb(): void {
    if (this.webPage) {
      window.open(this.webPage, "_blank");
    }
  }

  goToWhatsApp(): void {
    if (this.whatsapp) {
      const message = encodeURIComponent(
        "¡Hola! Me gustaría obtener más información sobre sus servicios."
      ); // Personaliza el mensaje
      window.open(`https://wa.me/${this.whatsapp}?text=${message}`, "_blank");
    }
  }
}
